import React, { useState } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { useSEO } from './useSEO';

function safeNext(value) {
  if (!value || typeof value !== 'string') return null;
  if (!value.startsWith('/') || value.startsWith('//')) return null;
  return value;
}

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const next = safeNext(params.get('next'));
  const [form, setForm] = useState({ email: '', password: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showPassword, setShowPassword] = useState(false);

  useSEO({
    title: 'Log in',
    description: 'Sign in to access GCSE and A-Level revision resources, past papers, mark schemes, and study tools.',
    canonicalPath: '/login'
  });

  const onChange = (e) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
    setError(null);
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      await login(form.email.trim().toLowerCase(), form.password);
      navigate(next || '/timetable', { replace: true });
    } catch (err) {
      setError(err.message || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-wrapper">
      <div className="auth-card">
        <h2>Log in</h2>
        <form onSubmit={onSubmit} className="auth-form" noValidate>
          <label>
            Email
            <input
              type="email"
              name="email"
              autoComplete="email"
              autoCapitalize="off"
              autoCorrect="off"
              spellCheck="false"
              inputMode="email"
              required
              value={form.email}
              onChange={onChange}
              maxLength={254}
              placeholder="you@example.com"
            />
          </label>
          <label>
            Password
            <div className="auth-pw-wrap">
              <input
                type={showPassword ? 'text' : 'password'}
                name="password"
                autoComplete="current-password"
                required
                value={form.password}
                onChange={onChange}
              />
              <button
                type="button"
                className="auth-pw-toggle"
                onClick={() => setShowPassword((s) => !s)}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
                tabIndex={-1}
              >{showPassword ? 'Hide' : 'Show'}</button>
            </div>
          </label>
          {error && <p className="auth-error" role="alert">{error}</p>}
          <button type="submit" disabled={loading} className="primary-btn auth-submit">
            {loading ? 'Signing in…' : 'Sign in'}
          </button>
        </form>
        <p className="auth-foot">
          No account? <Link to={next ? `/register?next=${encodeURIComponent(next)}` : '/register'}>Create one</Link>
        </p>
      </div>
    </div>
  );
}
